import React, { useEffect, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import { SIGNUP, SIGNIN } from '../../../../routes/paths';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MasterMessage } from '../../../../masters/masterMessages';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getUserLocation } from '../../../components/location/getUserLocation';
import { RootState } from '../../../../redux/store';
import { asyncAuthLogin } from '../../../../services/auth.service';
import { useAuthContext } from '../../../../contexts/authContext';
import { Copyright } from '../../../components/copyright/copyright';
import { types } from '../../../../redux/types';
import { ToastSwal } from '../../../components/general/toastSwal/toastSwal';
import { validationsSignIn, valuesSignIn } from './formikData';
import AutoCompleteField from '../../../components/general/fields/autoCompleteField';
import FieldText from '../../../components/general/fields/fieldText';
import { ISignIn } from '../../../../shared/types/auth';
import LoadingButtonGeneral from '../../../components/general/buttons/loadingButtonGeneral';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';

export default function SignIn({ showRegister, showChangePass }: ISignIn) {
    const navigate = useNavigate();
    const dispatch: any = useDispatch();
    const { login }: any = MasterMessage.auth;
    const { geolocation } = useSelector((state: RootState) => state.auth);
    const { channelList } = useSelector((state: RootState) => state.general);
    const { logout } = useAuthContext();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [loading, setLoading] = useState(false);

    const getReCaptcha = async () => {
        if (executeRecaptcha) {
            const localToken = await executeRecaptcha('someAction');
            return localToken;
        }
    };

    const formik = useFormik({
        initialValues: valuesSignIn,
        validationSchema: validationsSignIn,
        validateOnBlur: true,
        onSubmit: async (formSignIn) => {
            setLoading(true);
            const captcha = (await getReCaptcha()) || '';
            if (!geolocation) {
                ToastSwal.fire({
                    text: login.messageGeolocalizationSignIn,
                    icon: 'error',
                    iconColor: '#1d1d1d',
                });
                setLoading(false);
            } else {
                asyncAuthLogin({ ...formSignIn, captcha })
                    .then((body: any) => {
                        if (body.message !== undefined) {
                            ToastSwal.fire({
                                text:
                                    login[body.message] ||
                                    login['AUTH-NO-SIGIN'],
                                icon: 'error',
                                iconColor: '#1d1d1d',
                            });
                        } else {
                            dispatch({
                                type: types.authLogin,
                                payload: body,
                            });
                        }
                    })
                    .finally(() => setLoading(false));
            }
        },
    });

    useEffect(() => {
        dispatch({
            type: types.logoutLogin,
            payload: '',
        });
        logout();
    }, []);

    useEffect(() => {
        if (!navigator.geolocation) {
            ToastSwal.fire({
                text: login.messageGeolocalizationSignIn,
                icon: 'error',
                iconColor: '#1d1d1d',
            });
        } else {
            getUserLocation().then((lngLat) =>
                dispatch({
                    type: types.validationGeolocation,
                    payload: lngLat,
                })
            );
        }
    }, [dispatch, login.messageGeolocalizationSignIn]);

    return (
        <>
            <Box
                sx={{
                    margin: '0 auto auto',
                    width: { xs: 260, sm: 360, md: 380, lg: 380 },
                    height: '100%',
                    boxShadow: 5,
                    borderRadius: 3,
                    px: 3,
                    py: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Avatar
                    sx={{
                        m: 1,
                        bgcolor: 'primary.main',
                        width: 60,
                        height: 60,
                    }}
                    variant="rounded"
                >
                    <LockOutlinedIcon fontSize="large" />
                </Avatar>
                <Typography component="h1" variant="h5" sx={{ mt: 1 }}>
                    Iniciar Sesión
                </Typography>
                <Box component="form" noValidate sx={{ mt: 1 }}>
                    <Grid container spacing={0.5} mt={1}>
                        <Grid item xs={12}>
                            <FieldText
                                label="Correo"
                                name="username"
                                required={true}
                                margin="normal"
                                autoComplete="email"
                                touched={formik.touched.username}
                                errors={formik.errors.username}
                                value={formik.values.username}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} mt={0.5}>
                            <FieldText
                                label="Contraseña"
                                name="password"
                                required={true}
                                autoComplete="current-password"
                                type="password"
                                touched={formik.touched.password}
                                errors={formik.errors.password}
                                value={formik.values.password}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AutoCompleteField
                                data={channelList}
                                label="Selecciona un canal"
                                name="channel"
                                margin="normal"
                                touched={formik.touched.channel}
                                errors={formik.errors.channel}
                                value={formik.values.channel}
                                onChangeValue={(value) =>
                                    formik.setFieldValue('channel', value)
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ textAlign: 'center' }}>
                            <LoadingButtonGeneral
                                endIcon={<LoginRoundedIcon color="secondary" />}
                                onClick={formik.handleSubmit}
                                title="Ingresar"
                                fullWidth={true}
                                loading={loading}
                                loadingPosition="end"
                                sx={{ mt: 1, mb: 2 }}
                                sxTitle={{
                                    fontSize: { lg: 15, md: 13, xs: 11 },
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Grid container>
                    <Grid item xs>
                        {showChangePass && (
                            <Link
                                onClick={() => navigate(SIGNIN)}
                                variant="body2"
                                type="submit"
                            >
                                Recuperar contraseña
                            </Link>
                        )}
                    </Grid>
                    <Grid item>
                        {showRegister && (
                            <Link
                                onClick={() => navigate(SIGNUP)}
                                variant="body2"
                                type="submit"
                            >
                                Registrarse
                            </Link>
                        )}
                    </Grid>
                </Grid>
            </Box>
            <Copyright sx={{ position: 'relative', mt: 3 }} />
        </>
    );
}
